import { DiContainer, Injectable } from '@jkhy/frontend-utils/di';

import { AchExceptionDetailModelDto, DashboardClient } from '@treasury/api/channel';
import { AnalyticsEvent, AnalyticsEventPayloads, AnalyticsService } from '@treasury/core/analytics';
import { AchExceptionDetail, PayOrReturnChoice } from '../../mappings/ach';
import AchFilterRequests from '../../requests/ach/ach-filter-requests';
import { PositivePayExceptionRequests } from '../../requests/positive-pay/positive-pay-exception-requests.js';
import { AchExceptionDetailDto, AchExceptionsQueryDto } from '../../types';
import { AchExceptionDetailForApiDto } from '../../types/arp/ach-exception-detail-for-api.dto.js';
import { AchExceptionDetail as PosPayAchExceptionDetail } from '../positive-pay/entities/ach-exception-detail';
@Injectable()
export class AchExceptionsService {
    constructor(
        private readonly analyticsService: AnalyticsService,
        private readonly dashboardClient: DashboardClient
    ) {}

    public static async findAchExceptions(filter: AchExceptionsQueryDto) {
        const exceptions = await AchFilterRequests.findAchExceptions(filter);
        let recordCount = 0;
        if (exceptions?.achExceptionDetails) {
            recordCount = exceptions.achExceptionDetails.length;
        } else {
            exceptions.achExceptionDetails = [];
        }
        return {
            data: (exceptions.achExceptionDetails as AchExceptionDetailDto[]).map(
                dto => new AchExceptionDetail(dto)
            ),
            totalCount: recordCount,
        };
    }

    /**
     * @deprecated Use the instance method `saveAchExceptions` instead. This static method will be removed in a future version.
     */
    public static saveAchExceptions(records: AchExceptionDetailForApiDto[]) {
        return Promise.all([
            PositivePayExceptionRequests.saveAchExceptions(records),
            DiContainer.getInstance().then(di => di.getAsync(AnalyticsService)),
        ]).then(([result, analytics]) => {
            trackExceptionDecisions(records, analytics);
            return result;
        });
    }

    /**
     * Saves ACH exception decisions.
     *
     * This method takes an array of ACH exception details, converts them to the required DTO format,
     * sends them to the backend for processing, and logs the decisions to analytics.
     *
     * @param records An array of {@link PosPayAchExceptionDetail} objects representing the ACH exceptions to be decisioned.
     * @returns A promise that resolves to an object containing:
     *  - `totalDecisionsCount`: The total number of decisions processed.
     *  - `failedDecisionsCount`: The number of decisions that failed.
     *  - `failedAchExceptions`: An array of ACH exceptions that failed to be decisioned.
     */

    public async saveAchExceptions(records: PosPayAchExceptionDetail[]) {
        const decisions = records.map(
            record => record.toSaveDto() as unknown as AchExceptionDetailModelDto
        );
        const result = await this.dashboardClient.dashboardApproveOrRejectAchExceptions(decisions);

        const dtos = records.map(record => {
            return {
                ...record.toDto(),
                amount: record.amount || 0,
                achExceptionDecisionStatusId: record.decisionChoice,
                exceptionType: record.exceptionType,
                secCode: record.secCode,
                accountType: record.accountType,
            } as unknown as AchExceptionDetailForApiDto;
        });

        // Log decisions to analytics
        trackExceptionDecisions(dtos, this.analyticsService);
        const { data } = result;
        return {
            totalDecisionsCount: data.totalDecisionsCount,
            failedDecisionsCount: data.failedDecisionsCount,
            failedAchExceptions: data.failedAchExceptions,
        };
    }
}

type AchDecisionPayload = AnalyticsEventPayloads[AnalyticsEvent.AchExceptionDecision];
type AchDecision = AchDecisionPayload['decision'];
type AchExceptionType = AchDecisionPayload['exceptionType'];
type AchSecCode = AchDecisionPayload['secCode'];
type AchAccountType = AchDecisionPayload['accountType'];

function trackExceptionDecisions(
    decisions: AchExceptionDetailForApiDto[],
    analytics: AnalyticsService
) {
    decisions.forEach(d => {
        const { amount, achExceptionDecisionStatusId, exceptionType, secCode, accountType } = d;
        let decision: AchDecision = 'none';

        switch (achExceptionDecisionStatusId) {
            case PayOrReturnChoice.Pay:
                decision = 'pay';
                break;
            case PayOrReturnChoice.Return:
                decision = 'return';
                break;
            case PayOrReturnChoice.Unselected:
            default:
                decision = 'none';
                break;
        }

        analytics.track(AnalyticsEvent.AchExceptionDecision, {
            amount: amount || 0,
            decision,
            exceptionType: exceptionType as AchExceptionType,
            secCode: secCode as AchSecCode,
            accountType: accountType as AchAccountType,
        });
    });
}
